import styled from "styled-components";
import { Version } from "../note-history.types";
import { CardItem } from "./CardItem";
import { useMemo, useRef } from "react";
import { useScrollDetection } from "@/hooks";

interface VersionWithNumber extends Version {
  versionNumber: number;
  isCurrentVersion: boolean;
}

interface VersionFeedProps {
  className?: string;
  versions?: VersionWithNumber[];
  userName?: string;
  onItemSelect?: (uuid: string) => void;
  selectedVersionUuid?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  searchTerm?: string;
}

export const VersionFeed = styled(
  ({
    className,
    versions = [],
    userName,
    onItemSelect,
    selectedVersionUuid,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading = false,
    searchTerm = "",
  }: VersionFeedProps) => {
    const listOuterRef = useRef<HTMLDivElement>(null);
    const listInnerRef = useRef<HTMLDivElement>(null);
    // Simple scroll handler for infinite loading
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

      // Trigger when user is 200px from bottom
      if (
        scrollHeight - scrollTop <= clientHeight + 200 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage?.();
      }
    };

    const isScrollActive = useScrollDetection({
      innerRef: listInnerRef,
      outerRef: listOuterRef,
    });

    return (
      <div
        className={className + " " + (isScrollActive ? "scroll-active" : "")}
        ref={listOuterRef}
        onScroll={handleScroll}
      >
        <div className="versions-list" ref={listInnerRef}>
          {isLoading ? (
            <div className="loading-indicator">Loading versions...</div>
          ) : versions.length > 0 ? (
            <>
              {versions.map((version: VersionWithNumber) => (
                <CardItem
                  version={version}
                  userName={userName}
                  versionNumber={version.versionNumber}
                  key={version.uuid}
                  onClick={(version: Version) => onItemSelect?.(version.uuid)}
                  isSelected={version.uuid === selectedVersionUuid}
                />
              ))}
              {isFetchingNextPage && (
                <div className="loading-indicator">
                  Loading more versions...
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              {searchTerm
                ? `No versions found matching "${searchTerm}"`
                : "No version history available for this note."}
            </div>
          )}
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;

  &.scroll-active {
    padding-right: 5px;
  }

  .versions-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1 1 auto;
    min-height: 0;

    .loading-indicator {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 12px;
      font-size: 12px;
      color: ${({ theme }) => theme.palette.text.secondary};
      font-style: italic;
    }

    .empty-state {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 24px 12px;
      font-size: 14px;
      color: ${({ theme }) => theme.palette.text.secondary};
      font-style: italic;
      text-align: center;
    }
  }
`;
